import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { checksFromJson } from "../utils/apiChecks";

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 13 };
const ellipsis = { maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/**
 * The API editor's "From last response": every value of the request's last JSON response
 * as a "JSON value equals …" check, all ticked; the user unticks what they don't want
 * (tokens, ids, dates) and onAdd gets the chosen checks.
 */
export default function ChecksFromResponseDialog({ open, json, existingChecks, onClose, onAdd }) {
	const { rows, more } = useMemo(() => (open ? checksFromJson(json, existingChecks) : { rows: [], more: 0 }), [open, json, existingChecks]);
	const [chosen, setChosen] = useState(new Set());
	useEffect(() => {
		if (open) setChosen(new Set(rows.map((r) => r.path)));
	}, [open, rows]);

	const toggle = (path) =>
		setChosen((current) => {
			const next = new Set(current);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});
	const allChosen = rows.length > 0 && chosen.size === rows.length;

	const add = () =>
		onAdd(rows.filter((r) => chosen.has(r.path)).map((r) => ({ source: "jsonPath", path: r.path, operator: "equals", expected: r.expected })));

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Add checks from the last response</DialogTitle>
			<DialogContent dividers>
				{rows.length === 0 ? (
					<Typography color="text.secondary">Every value in the last response already has a check.</Typography>
				) : (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							Each ticked value becomes a check that it equals what came back this time. Untick values that change between runs,
							such as tokens, ids, dates and counters. You can still edit or delete the checks afterwards; nothing is saved until you
							click Save.
						</Typography>
						{more > 0 && (
							<Alert severity="info" sx={{ mb: 1.5 }}>
								The response has {more} more values than are listed here; add checks for those by hand.
							</Alert>
						)}
						<TableContainer sx={{ maxHeight: 420 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												size="small"
												checked={allChosen}
												indeterminate={chosen.size > 0 && !allChosen}
												onChange={() => setChosen(allChosen ? new Set() : new Set(rows.map((r) => r.path)))}
												inputProps={{ "aria-label": "Select all" }}
											/>
										</TableCell>
										<TableCell sx={{ width: "45%" }}>JSON path</TableCell>
										<TableCell>Equals</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((r) => (
										<TableRow key={r.path} hover onClick={() => toggle(r.path)} sx={{ cursor: "pointer" }}>
											<TableCell padding="checkbox">
												<Checkbox size="small" checked={chosen.has(r.path)} inputProps={{ "aria-label": r.path }} />
											</TableCell>
											<TableCell sx={{ ...mono, ...ellipsis }} title={r.path}>
												{r.path}
											</TableCell>
											<TableCell sx={{ ...mono, ...ellipsis }} title={r.expected}>
												{r.expected === "" ? <Box component="em" sx={{ color: "text.secondary" }}>(empty)</Box> : r.expected}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" onClick={add} disabled={chosen.size === 0}>
					Add {chosen.size} {chosen.size === 1 ? "check" : "checks"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
